import { getApiUrl } from '../../utils/shared/helpers';
import { formatDate } from '../../utils/shared/normalize';
import { checkStatus, handleError } from '../../utils/api';
import { EligibilityCheck } from '../../interfaces/register';
import {
  CoverageCheckValues,
  GuestEligibilityCheckApi,
} from '../../interfaces/eligibility';

// Maps the Member-Details form (affiliation + member_id + dependent/primary_*) plus the
// carry-store demographics into the snake_case body; birthdates are sent as ISO yyyy-mm-dd.
const mapToApi = (
  values: EligibilityCheck,
  demographics: CoverageCheckValues
): GuestEligibilityCheckApi => ({
  affiliation_id: values.affiliationId,
  dependent: values.isDependent,
  member_id: values.memberId,
  primary_first_name: values.primaryFirstName,
  primary_last_name: values.primaryLastName,
  primary_gender: values.primaryGender,
  primary_member_id: values.primaryMemberId,
  primary_birthdate:
    values.primaryDobDay &&
    values.primaryDobMonth &&
    values.primaryDobYear &&
    formatDate(
      `${values.primaryDobMonth}/${values.primaryDobDay}/${values.primaryDobYear}`,
      'iso'
    ),
  relationship: values.relationship,
  first_name: demographics.firstName.trim(),
  last_name: demographics.lastName.trim(),
  gender: demographics.gender,
  birthdate: formatDate(
    `${demographics.dobMonth}/${demographics.dobDay}/${demographics.dobYear}`,
    'iso'
  ),
  zip: demographics.zip,
});

// Unauthenticated guest 270 submit for the coverage-check pre-registration flow. No token, so
// fetch directly; returns the same { error, eligibilityCheck } shape as postEligibilityCheckAsync.
// Both arguments are PII/PHI — never log them or place them in a URL/query.
const postGuestEligibilityCheckAsync = async (
  values: EligibilityCheck,
  demographics: CoverageCheckValues
) => {
  const url = `${getApiUrl()}/api/v1/preauth_eligibility_check/external`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eligibility_check: mapToApi(values, demographics),
      }),
    });

    await checkStatus(response);

    const { eligibility_check: eligibilityCheck } = await response.json();
    return { error: null, eligibilityCheck };
  } catch (error) {
    // Generic, PHI-free message — never interpolate the submitted values.
    handleError('unexpected status - guest eligibility check', error);
    return { error: true, eligibilityCheck: null };
  }
};

export default postGuestEligibilityCheckAsync;
